// dashboard.repo — data untuk metrik periode + filter karyawan/cabang + panel target.
// finScope: kecualikan hanya arsip_keu (tutup buku); arsip operasional TETAP dihitung
// (konsisten v0.18 dashBaseList/dashRevList = finScope).
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { MONEY_COLS, rowToTicket, fetchRounds, type TicketRow } from '@core/ticketMap';
import { omzetTiket, modalTiket, biayaSimpanTiket } from '@domain/money';

export interface DashRow extends TicketRow {
  penerima: string | null; servis_ulang: boolean | null; rating: number | null; survey_done: boolean | null;
  customers?: { kat: 'User' | 'Toko' | null } | { kat: 'User' | 'Toko' | null }[] | null;
}

/**
 * Seluruh tiket untuk metrik Dashboard.
 *
 * WAJIB lewat fetchAllPaged: PostgREST memotong hasil di 1.000 baris TANPA memberi tanda apa pun,
 * jadi tanpa paging seluruh angka Dashboard diam-diam salah begitu toko melewati seribu servis —
 * dan salahnya ke arah "kelihatan lebih sepi", yang justru tidak memancing curiga.
 */
export async function fetchRows(): Promise<DashRow[]> {
  return fetchAllPaged<DashRow>((dari, sampai) => supabase().from('tickets')
    .select(`${MONEY_COLS},penerima,servis_ulang,rating,survey_done,customers(kat)`)
    .eq('arsip_keu', false).order('no').range(dari, sampai));
}

export interface DashEmp { id: number; nama: string; role: string; outlet: string | null }
/** Karyawan untuk filter "Semua karyawan" (Teknisi/CS/SPV/Manager — sama dgn v0.18 whoOpts). */
export async function listEmployees(): Promise<DashEmp[]> {
  const { data, error } = await supabase().from('employees').select('id,nama,role,outlet')
    .eq('aktif', true).in('role', ['Teknisi', 'CS', 'SPV', 'Manager']).order('nama');
  if (error) throw new Error(error.message);
  return (data ?? []) as DashEmp[];
}

export interface DashOutlet { kode: string; nama: string }
export async function listOutlets(): Promise<DashOutlet[]> {
  const { data, error } = await supabase().from('outlets').select('kode,nama').order('kode');
  if (error) throw new Error(error.message);
  return (data ?? []) as DashOutlet[];
}

export interface TrenBulan { ym: string; income: number; hpp: number; beban: number; laba: number; ekuitas: number }

/**
 * Tren 12 bulan (grafik Dashboard): income (kas diterima + biaya simpan) · beban (pengeluaran
 * non-aset) · laba OPERASIONAL kotor · Δekuitas. Basis kas, per bulan tgl-ambil (tiket) /
 * tgl-catat (beban & modal), finScope.
 *
 * CATATAN JUJUR: ini gambaran ARAH bulanan, BUKAN laba-bersih akuntansi menu Keuangan —
 * penyusutan aset & pemulihan-kerugian-teknisi TIDAK ikut di sini, dan tiket yang sedang
 * dalam klaim garansi (tgl_ambil di-reset) sementara keluar dari bulannya. Angka pasti untuk
 * tutup buku tetap di menu 💰 Keuangan. Label grafik menyebut "operasional" agar tak dikira sama.
 */
export async function tren12Bulan(): Promise<TrenBulan[]> {
  const kini = new Date();
  const mulaiD = new Date(kini.getFullYear(), kini.getMonth() - 11, 1);
  const mulai = mulaiD.toISOString().slice(0, 10);

  const [rows, bebanRows, eq] = await Promise.all([
    fetchAllPaged<TicketRow>((a, b) => supabase().from('tickets').select(MONEY_COLS)
      .eq('status', 'Sudah Diambil').eq('arsip_keu', false)
      .gte('tgl_ambil_iso', mulai).not('tgl_ambil_iso', 'is', null).order('no').range(a, b)),
    fetchAllPaged<{ tgl_iso: string; jumlah: number; is_aset: boolean | null }>((a, b) =>
      supabase().from('expenses').select('tgl_iso,jumlah,is_aset').gte('tgl_iso', mulai).order('id').range(a, b)),
    supabase().from('equity_moves').select('tgl,jenis,jumlah').gte('tgl', mulai),
  ]);
  const rounds = await fetchRounds(rows.map((r) => r.no));

  const m = new Map<string, TrenBulan>();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(kini.getFullYear(), kini.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    m.set(ym, { ym, income: 0, hpp: 0, beban: 0, laba: 0, ekuitas: 0 });
  }
  for (const r of rows) {
    const cur = m.get((r.tgl_ambil_iso ?? '').slice(0, 7)); if (!cur) continue;
    const t = rowToTicket(r, rounds.get(r.no) ?? []);
    cur.income += omzetTiket(t) + biayaSimpanTiket(t); cur.hpp += modalTiket(t);
  }
  for (const b of bebanRows) {
    if (b.is_aset) continue;                                   // belanja aset bukan beban
    const cur = m.get((b.tgl_iso ?? '').slice(0, 7)); if (cur) cur.beban += b.jumlah ?? 0;
  }
  // Δekuitas = laba periode + setoran modal − prive. Laba dihitung setelah beban terkumpul.
  const setoran = new Map<string, number>();
  for (const e of ((eq.data ?? []) as { tgl: string; jenis: string; jumlah: number }[])) {
    const ym = (e.tgl ?? '').slice(0, 7);
    setoran.set(ym, (setoran.get(ym) ?? 0) + (e.jenis === 'setoran' ? (e.jumlah ?? 0) : -(e.jumlah ?? 0)));
  }
  for (const cur of m.values()) {
    cur.laba = cur.income - cur.hpp - cur.beban;
    cur.ekuitas = cur.laba + (setoran.get(cur.ym) ?? 0);
  }
  return [...m.values()];
}

/** Baris beban (tgl, jumlah, aset?) untuk kartu Total Beban — disaring per periode di view. */
export async function bebanRows(): Promise<{ tgl_iso: string; jumlah: number; is_aset: boolean; outlet: string | null }[]> {
  // Paging: tanpa ini beban berhenti dihitung setelah 1.000 baris dan Total Beban diam-diam mengecil.
  // Kegagalan tetap dimaafkan (kolom belum ada / tak berhak) supaya dashboard tak ikut mati.
  try {
    return await fetchAllPaged<{ tgl_iso: string; jumlah: number; is_aset: boolean; outlet: string | null }>(
      (dari, sampai) => supabase().from('expenses').select('tgl_iso,jumlah,is_aset,outlet')
        .order('tgl_iso').range(dari, sampai));
  } catch { return []; }
}
